import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { isChunkLoadError } from "@/lib/lazy-with-retry";

interface FallbackProps {
  error: Error;
  resetError: () => void;
}

function ErrorFallback({ error, resetError }: FallbackProps) {
  const stale = isChunkLoadError(error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
          {stale ? (
            <RefreshCw className="w-8 h-8 text-accent" />
          ) : (
            <AlertTriangle className="w-8 h-8 text-destructive" />
          )}
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            {stale ? "Der er kommet en ny version af siden" : "Noget gik galt"}
          </h1>
          <p className="text-muted-foreground">
            {stale
              ? "Genindlæs siden for at hente den nyeste version."
              : "Der opstod en uventet fejl. Vores team er blevet underrettet."}
          </p>
        </div>

        {import.meta.env.DEV && (
          <div className="bg-muted rounded-lg p-4 text-left">
            <p className="text-sm font-mono text-destructive break-all">
              {error.message}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {stale ? (
            <Button onClick={() => window.location.reload()} variant="default">
              <RefreshCw className="w-4 h-4 mr-2" />
              Genindlæs
            </Button>
          ) : (
            <Button onClick={resetError} variant="default">
              <RefreshCw className="w-4 h-4 mr-2" />
              Prøv igen
            </Button>
          )}
          <Button 
            onClick={() => window.location.href = "/"} 
            variant="outline"
          >
            Gå til forsiden
          </Button>
        </div>
      </div>
    </div>
  );
}


export function SentryErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback 
          error={error instanceof Error ? error : new Error(String(error))} 
          resetError={resetError} 
        />
      )}
      beforeCapture={(scope) => {
        scope.setTag("location", window.location.href);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
